import {z} from 'zod';
import {workflowUserVariableDeclarationSchema} from './workflow-variable-declaration';
import {
  agenticWorkflowIdSchema,
  objectIdStringSchema,
  workflowDraftIdSchema,
  workflowDraftMutationIdSchema,
  workflowVersionIdSchema,
} from './agentic-workflow-identifiers';

export const workflowDraftStatuses = ['ready'] as const;

export const workflowDraftStatusSchema = z.enum(workflowDraftStatuses);

export type WorkflowDraftStatus = z.infer<typeof workflowDraftStatusSchema>;

export const safeWorkflowDraftConnectionTargetSchema = z
  .object({
    node: z.string().min(1),
    type: z.string().min(1),
    index: z.number().int().nonnegative(),
  })
  .strict();

export type SafeWorkflowDraftConnectionTarget = z.infer<
  typeof safeWorkflowDraftConnectionTargetSchema
>;

export const safeWorkflowDraftConnectionsSchema = z.record(
  z.record(z.array(z.array(safeWorkflowDraftConnectionTargetSchema)))
);

export type SafeWorkflowDraftConnections = z.infer<
  typeof safeWorkflowDraftConnectionsSchema
>;

export const safeWorkflowDraftNodeSchema = z
  .object({
    id: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    type: z.string().min(1).max(200),
    typeVersion: z.number().positive().optional(),
    position: z.tuple([z.number(), z.number()]),
    parameters: z.record(z.unknown()).default({}),
    disabled: z.boolean().optional(),
    // This durable Webflow-owned reference is never sent to n8n. It identifies
    // the reusable connection selected for this node's active workflow usage.
    integrationConnectionId: objectIdStringSchema.optional(),
  })
  .strict();

export type SafeWorkflowDraftNode = z.infer<typeof safeWorkflowDraftNodeSchema>;

/** Removes Webflow-only connection selection data before an n8n API call. */
export function toRuntimeWorkflowDraftNode(
  node: SafeWorkflowDraftNode
): Omit<SafeWorkflowDraftNode, 'integrationConnectionId'> {
  const {integrationConnectionId: _integrationConnectionId, ...runtimeNode} =
    node;
  return runtimeNode;
}

export const safeWorkflowDraftSettingsSchema = z
  .object({
    executionTimeout: z.number().int().positive().optional(),
    timezone: z.string().min(1).max(100).optional(),
  })
  .strict();

export type SafeWorkflowDraftSettings = z.infer<
  typeof safeWorkflowDraftSettingsSchema
>;

export const agentWorkflowDefinitionSchema = z
  .object({
    name: z.string().min(1).max(200),
    nodes: z.array(safeWorkflowDraftNodeSchema),
    connections: safeWorkflowDraftConnectionsSchema,
    settings: safeWorkflowDraftSettingsSchema.optional(),
    variableDeclarations: z
      .array(workflowUserVariableDeclarationSchema.strict())
      .default([]),
  })
  .strict();

export type AgentWorkflowDefinition = z.infer<
  typeof agentWorkflowDefinitionSchema
>;

export const createWorkflowDraftRequestSchema = z
  .object({
    mutationId: workflowDraftMutationIdSchema,
    definition: agentWorkflowDefinitionSchema,
  })
  .strict();

export type CreateWorkflowDraftRequest = z.infer<
  typeof createWorkflowDraftRequestSchema
>;

export const replaceWorkflowDraftRequestSchema = z
  .object({
    mutationId: workflowDraftMutationIdSchema,
    expectedRevision: z.number().int().positive(),
    definition: agentWorkflowDefinitionSchema,
  })
  .strict();

export type ReplaceWorkflowDraftRequest = z.infer<
  typeof replaceWorkflowDraftRequestSchema
>;

export const discardWorkflowDraftRequestSchema = z
  .object({
    mutationId: workflowDraftMutationIdSchema,
    expectedRevision: z.number().int().positive(),
  })
  .strict();

export type DiscardWorkflowDraftRequest = z.infer<
  typeof discardWorkflowDraftRequestSchema
>;

export const saveWorkflowDraftRequestSchema = z
  .object({
    mutationId: workflowDraftMutationIdSchema.describe(
      'Reuse this mutation ID when retrying an interrupted Save'
    ),
    expectedRevision: z.number().int().positive(),
    notes: z.string().trim().max(1000).optional(),
  })
  .strict();

export type SaveWorkflowDraftRequest = z.infer<
  typeof saveWorkflowDraftRequestSchema
>;

export const saveWorkflowDraftResponseSchema = z
  .object({
    workflowId: agenticWorkflowIdSchema,
    draftId: workflowDraftIdSchema,
    draftRevision: z.number().int().positive(),
    versionId: workflowVersionIdSchema,
    isActiveVersion: z.boolean(),
    savedOn: z.string().datetime(),
    savedByUserId: objectIdStringSchema,
  })
  .strict();

export type SaveWorkflowDraftResponse = z.infer<
  typeof saveWorkflowDraftResponseSchema
>;

export const workflowDraftSchema = z
  .object({
    draftId: workflowDraftIdSchema,
    workflowId: agenticWorkflowIdSchema,
    revision: z.number().int().positive(),
    status: workflowDraftStatusSchema,
    definition: agentWorkflowDefinitionSchema,
    createdOn: z.string().datetime(),
    updatedOn: z.string().datetime(),
  })
  .strict();

export type WorkflowDraft = z.infer<typeof workflowDraftSchema>;
